import React, { useEffect, useState } from "react";
import * as Sentry from "@sentry/react-native";
import { Alert, Pressable, StyleSheet, Text, View } from "react-native";
import { router } from "expo-router";
import FontAwesome from "@expo/vector-icons/FontAwesome";

import type { AppUserSession } from "@/schemas/auth/session";
import MediaService from "@/services/user/media";
import {
  type UserImageMedia,
  type UserLikableImageMedia,
} from "@/schemas/user/image";
import UserImageModal from "@/Components/Media/User/Images/UserImageModal";
import { ImageOptionAction } from "@/Components/Media/User/Images/UserImageOptions";
import { formatNumberForDisplay } from "@/formatting/number";
import { themes } from "@/Components/Styling/themeConfigs";
import InteractionService from "@/services/user/interaction";

type Props = {
  session: AppUserSession;
  imageMedia: UserLikableImageMedia;
  onLikeChange: () => void;
};

function ImageHeaderCard({
  session,
  imageMedia,
  onLikeChange,
}: Props): React.JSX.Element {
  const interactionService = new InteractionService(session);

  const [image] = useState<UserImageMedia>(imageMedia.image);
  const [isLiked, setIsLiked] = useState(imageMedia.isLiked);

  const [likeCount, setLikeCount] = useState(imageMedia.image.likeCount);
  const [isImageModalVisible, setIsImageModalVisible] = useState(false);

  const isReadOnly = session?.identity?.username !== image?.username;
  const allowedActions = [
    ImageOptionAction.CLOSE,
    isReadOnly ? ImageOptionAction.REPORT : ImageOptionAction.DELETE,
  ];

  const handleImageAction = async (
    action: ImageOptionAction,
  ): Promise<void> => {
    const mediaService = new MediaService(session);

    switch (action) {
      case ImageOptionAction.CLOSE:
        setIsImageModalVisible(false);
        break;
      case ImageOptionAction.DELETE:
        try {
          await mediaService.deleteImage(image.id);
          setIsImageModalVisible(false);
          router.back();
        } catch (error) {
          Alert.alert("Error", "Failed to delete image. Please try again.", [
            { text: "OK" },
          ]);
          Sentry.captureException(error, {
            extra: {
              message:
                "Failed to delete image in ImageHeaderCard handleImageAction",
            },
          });
        }
        break;
      default:
        setIsImageModalVisible(false);
    }
  };

  const handleLikeToggle = async (): Promise<void> => {
    // TODO: similar to PermissionScreen, modify local likes immediately for better UX?
    const newLikedState = !isLiked;
    setIsLiked(newLikedState);
    setLikeCount(likeCount + (newLikedState ? 1 : -1));

    try {
      if (newLikedState) {
        await interactionService.like(image);
      } else {
        await interactionService.unlike(image);
      }
      onLikeChange();
    } catch (error) {
      // Revert state if API call fails
      setIsLiked(!newLikedState);
      setLikeCount(likeCount + (newLikedState ? -1 : 1));

      Sentry.captureException(error, {
        extra: { message: "Failed to toggle like in ImageHeaderCard" },
      });

      Alert.alert("Error", "Something went wrong. Please try again.", [
        { text: "OK" },
      ]);
    }
  };

  useEffect(() => {
    setIsLiked(imageMedia.isLiked);
    setLikeCount(imageMedia.image.likeCount ?? 0);
  }, [imageMedia]);

  return (
    <>
      <View style={styles.imageActionBar}>
        <View style={styles.actionItem}>
          <Pressable onPress={() => handleLikeToggle()}>
            <View style={styles.actionItemIcon}>
              <FontAwesome
                name={isLiked ? "heart" : "heart-o"}
                size={20}
                color={isLiked ? "red" : "black"}
              />
            </View>
          </Pressable>
          {likeCount > 0 && (
            <Text style={styles.actionItemText}>
              {formatNumberForDisplay(likeCount)}{" "}
              {likeCount > 1 ? "likes" : "like"}
            </Text>
          )}
        </View>
      </View>
      {isImageModalVisible && (
        <UserImageModal
          image={image}
          allowedOptions={allowedActions}
          onOptionSelected={(action: ImageOptionAction) =>
            handleImageAction(action)
          }
        />
      )}
    </>
  );
}

const currentTheme = themes.morning;

const styles = StyleSheet.create({
  imageActionBar: {
    backgroundColor: "white", // TODO: create currentTheme.backgroundColor.mobile?
    flexDirection: "row",
    justifyContent: "flex-start",
    borderColor: currentTheme.borderColor,
    borderStyle: "solid",
    borderBottomWidth: 1,
  },
  actionItem: {
    alignItems: "center",
    flexDirection: "row",
  },
  actionItemIcon: {
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  actionItemText: {
    fontSize: currentTheme.typography.h2.size.mobile,
    textAlign: "left",
  },
});

export default ImageHeaderCard;
